import React, { useState, Fragment, useEffect, useReducer } from 'react'
import ReactDOM from 'react-dom'
import { oneOfType, array, string, func, bool, object } from 'prop-types'
import classnames from 'classnames'
import { FormattedMessage } from 'react-intl'
import { useFiltersState } from '../../providers/filters/filtersContext'
import { filterizer, sortingBy, objectToUrlPath, handleAsyncQuery } from '../../helpers/common'
import { EMPTY_ARRAY, EMPTY_OBJECT, PROFESSIONALS, IMAGES, ARTICLE_ARCHIVE } from '../../utils/constants'
import { sendClickEventToInsights } from '../../utils/insights'
import List from '../ui/list/List'
import Button from '../ui/button/Button'
import Dropdown from '../ui/dropdown/Dropdown'
import {
  UNIT_LABELS,
  AREA,
  MIN_AREA,
  MAX_AREA,
  COLOR,
  YEAR,
  MATERIALS,
  METER,
  FEET,
  TAG,
  AREA_FILTERS,
  BIG_FILTERS,
  SMALL_FILTERS,
  STANDALONE_FILTERS,
  ONE_BIG_COLUMN_FILTERS,
  CATEGORIES,
  OFFICES,
  BIM,
  KEYS_CAT_LEVEL_ONE,
  EXPERTISE,
  SMALL_ASCENDING_FILTERS,
  CATEGORIES_WITH_TWO_LEVELS,
  COUNTRY,
  STATES,
  ASYNC_FILTERS,
  PRODUCT_TYPE
} from './constants'
import ColorFilters from './ColorFilters'
import ButtonFilter from './ButtonFilter'
import AreaFilter from './AreaFilter'
import { messages, colorMessages } from './messages'
import {
  showInitialState, showReducer,
  filterInitialState, filterReducer,
  asyncFilterReducer, asyncFilterInitialState
} from './filtersReducer'
import {
  getAreaSelected,
  isCategoriesWithLevelsFilter,
  getFilterLabel,
  getCategoryItem,
  meterToFeet,
  feetToMeter
} from './helpers'
import { BigList, HeaderView, ContentView, MobileContent, MobileList, BigTreeList } from './partials'

const NAME = 'name'
const DOC_COUNT = 'doc_count'

/**
 * Component to render a filter
 *
 * @component
 * @example
 * return (
 *   <Filter
 *    data={[{name: "EGGER", slug: "egger", doc_count: 42}, {name: "Bona", slug: "bona", doc_count: 21}]}
 *    item='companies'
 *    isMobile={false}
 *    isInside={false}
 *    requestWithRouting={() => {}}
 *    onShowFilter={() => {}}
 *    contentClasses=''
 *    onResetFilters,
 *    searchType='projects'
 *   />
 * )
*/
const Filter = ({
  data = EMPTY_ARRAY,
  item,
  isMobile = false,
  isInside = false,
  requestWithRouting,
  onShowFilter,
  contentClasses,
  categoriesLabels = EMPTY_OBJECT,
  joinCategoriesLabels,
  setJoinCategoriesLabels,
  removeJoinCategoriesLabels,
  onResetFilters,
  searchType,
  categoriesTreeData = EMPTY_ARRAY,
  site = 'us'
}) => {
  let renderFilter = null
  let filteredData = EMPTY_ARRAY
  let descendingData = EMPTY_ARRAY
  const { filtersObject = EMPTY_OBJECT, activeCategories = EMPTY_OBJECT } = useFiltersState()
  const [{ modalOn, filterShow }, showDispatch] = useReducer(showReducer, showInitialState)
  const [{ activeFilter, itemSelected }, filterDispatch] = useReducer(filterReducer, filterInitialState)
  const [{ asyncData, loadingAsyncData }, asyncDataDispatch] = useReducer(asyncFilterReducer,
    { ...asyncFilterInitialState, asyncData: data })
  const [query, handleQuery] = useState('')
  const [forceOpen, setForceOpen] = useState(false)
  const isNotArea = item !== AREA
  const standaloneWithDropdown = [AREA, COLOR]
  const isBig = BIG_FILTERS.includes(item)
  const isCategoryItem = getCategoryItem(item, searchType)
  const isSmall = SMALL_FILTERS.includes(item)
  const containerClasses = classnames(
    'ad-filter',
    `ad-filter--${[item]}`,
    { 'ad-filter--full': isBig, 'ad-filter--inside': isInside }
  )
  const queryFilterized = filterizer(query)
  const renderNoMatchQuery = <FormattedMessage values={{ query }} {...messages.noResults} />
  const category = isCategoryItem ? { category: categoriesLabels.hasOwnProperty(item) ? categoriesLabels[item] : '' } : {}
  const itemLabel = item && <FormattedMessage values={{ ...category }} {...messages[getFilterLabel(item, searchType)]} />
  const isOneColumn = ONE_BIG_COLUMN_FILTERS.includes(item) || !!(categoriesTreeData.length > 0 && query)

  useEffect(() => {
    let selectedItem = EMPTY_OBJECT
    const splittedCat = item.split('_')
    if (isNotArea && data) {
      if (isCategoryItem && isCategoriesWithLevelsFilter(splittedCat[0], searchType)) {
        selectedItem = data.filter(a => (a.slug || a.path || a.name) === activeCategories[item])
      } else {
        selectedItem = data.filter(a => a.slug === filtersObject[item] || a.name === filtersObject[item])
      }
    }

    if (Object.keys(filtersObject).includes(item) || Object.keys(activeCategories).includes(item)) {
      const payload = { ...selectedItem[0] }
      filterDispatch({ type: 'SELECT_ITEM', payload })
    }

    if (!isNotArea) {
      const activeAreas = getAreaSelected(filtersObject)
      if (activeAreas.length > 0) {
        const payload = { ...activeAreas }
        filterDispatch({ type: 'SELECT_ITEM', payload })
      }
    }
  }, [])

  useEffect(() => {
    if (ASYNC_FILTERS.includes(item)) {
      if (!query.length) {
        asyncDataDispatch(({ type: 'READY', payload: data }))
      } else if (query.length > 2) {
        asyncDataDispatch({ type: 'FETCHING' })
        const [firstCategory] = item.split('_')
        const itemCategory = [CATEGORIES, EXPERTISE].includes(firstCategory) ? firstCategory : item
        const filters = objectToUrlPath(filtersObject, searchType, itemCategory)
        handleAsyncQuery(item, searchType, site, query, filters, data => asyncDataDispatch({ type: 'READY', payload: data }))
      }
    }
  }, [query])

  const onClickItem = (e, clickedItem, _index, parent) => {
    e.preventDefault()
    const [firstCategory] = item.split('_')
    const itemCategory = [CATEGORIES, EXPERTISE].includes(firstCategory) ? firstCategory : item
    if (item === TAG && !filtersObject.topic) {
      filtersObject.topic = parent.slug
    }
    const otherFilters = objectToUrlPath(filtersObject, searchType, itemCategory)
    let filters = `${otherFilters}/${[item]}/${clickedItem.slug || clickedItem.name || clickedItem}`
    if (isCategoryItem) {
      if (KEYS_CAT_LEVEL_ONE.includes(item)) {
        if (clickedItem.path.split('_').length > 1) {
          setJoinCategoriesLabels('joinCategoriesLabels', 'active')
        } else {
          joinCategoriesLabels && removeJoinCategoriesLabels('joinCategoriesLabels')
        }
      }
      filters = `${otherFilters}/${firstCategory}/${clickedItem.path}`
    }

    forceOpen && window.adInsights.e('search-project-cat-flyout', 'click', 'select', clickedItem.name)
    requestWithRouting(filters)
  }

  const onApplyAreaFilter = (e, values = EMPTY_OBJECT) => {
    e.preventDefault()
    let updatedValues = values
    const isMeter = localStorage.getItem(METER)
    if (!isMeter) {
      updatedValues = { min: feetToMeter(values.min), max: feetToMeter(values.max) }
    }
    const otherFilters = objectToUrlPath(filtersObject, searchType, item)
    const minAreaString = updatedValues.min !== '' ? `/${MIN_AREA}/${updatedValues.min}` : ''
    const maxAreaString = updatedValues.max !== '' ? `/${MAX_AREA}/${updatedValues.max}` : ''
    const filters = `${otherFilters}${minAreaString}${maxAreaString}`
    requestWithRouting(filters)
  }

  const resetSelectedItem = e => {
    e.stopPropagation()
    let itemToRemove = isNotArea ? item : AREA_FILTERS
    if (item === COUNTRY && searchType === PROFESSIONALS) {
      itemToRemove = [COUNTRY, STATES]
    }
    const filters = objectToUrlPath(filtersObject, searchType, itemToRemove)
    requestWithRouting(filters)
  }

  const handleMobileInsideFilterButton = (e, item) => {
    e.stopPropagation()
    if (activeFilter) {
      resetSelectedItem(e)
      showDispatch({ type: 'HIDE_FILTER' })
    } else {
      onShowFilter(item)
      showDispatch({ type: 'SHOW_FILTER' })
    }
  }

  if (isNotArea && filterShow) {
    const ascendingData = sortingBy(asyncData, NAME)
    const filteredList = filteredData => (<List onClick={onClickItem} isInnerHtml items={filteredData || EMPTY_ARRAY} />)
    let mostPopularFiltered = EMPTY_ARRAY
    let mostPopularData

    if (isBig) {
      filteredData = queryFilterized(ascendingData, false)
      if (categoriesTreeData.length > 0 && (item === TAG || (!query && !isMobile))) {
        const twoLevelsCat = CATEGORIES_WITH_TWO_LEVELS.includes(item)
        const bigTreeListProps = { messages, onClickItem, filteredData: categoriesTreeData, renderNoMatchQuery, twoLevelsCat }
        if (item === TAG) {
          bigTreeListProps.titleKey = 'topics'
          bigTreeListProps.onMainCategoryClick = (e, clickedItem) => {
            e.preventDefault()
            const otherFilters = objectToUrlPath(filtersObject, searchType, ['topic', 'tag'])
            let filters = `/topic/${clickedItem.slug}`
            if (otherFilters) filters += otherFilters
            forceOpen && window.adInsights.e('search-archive-tag-flyout', 'click', 'select', clickedItem.name)
            requestWithRouting(filters)
          }
          bigTreeListProps.activeSubcategory = filtersObject.topic
        }
        renderFilter = <BigTreeList {...bigTreeListProps} />
      } else {
        const maxPopularData = item === OFFICES ? 52 : 13
        mostPopularData = sortingBy(data, DOC_COUNT, true).slice(0, maxPopularData)
        mostPopularFiltered = queryFilterized(mostPopularData, false)
        const isOneColumnType = isOneColumn ? item : ''
        const bigListProps = { messages, mostPopularFiltered, onClickItem, filteredData, renderNoMatchQuery, isOneColumnType, site }
        renderFilter = <BigList activeSearch={!!query.length} {...bigListProps} />
      }
    }

    if (item === YEAR) {
      descendingData = sortingBy(data, NAME, true, true).map(y => ({ ...y, name: y.name.toString() }))
      filteredData = queryFilterized(descendingData, true)
      renderFilter = (!isMobile || searchType === ARTICLE_ARCHIVE) && filteredList(filteredData)
    }
    if (SMALL_ASCENDING_FILTERS.includes(item)) {
      filteredData = queryFilterized(ascendingData)
      renderFilter = filteredList(filteredData)
    }
  }

  if (!isInside) {
    if (item === AREA) {
      renderFilter = <AreaFilter onApply={onApplyAreaFilter} {...data} />
    }
  }

  if (!isMobile || searchType === IMAGES) {
    if (item === COLOR) {
      renderFilter = (
        <ColorFilters
          onClick={onClickItem}
          onReset={resetSelectedItem}
          activeColor={filtersObject.hasOwnProperty(COLOR) ? filtersObject[item] : ''}
        />
      )
    }

    if (item === BIM) {
      const isActive = !!filtersObject.hasOwnProperty(BIM)
      renderFilter = (
        <ButtonFilter
          active={isActive}
          label={itemLabel}
          onClick={isActive ? resetSelectedItem : e => onClickItem(e, 'active')}
        />
      )
    }
  }

  let buttonLabel = itemLabel
  if (itemSelected.hasOwnProperty(NAME)) {
    if (activeCategories[item]) {
      buttonLabel = KEYS_CAT_LEVEL_ONE.includes(item) && joinCategoriesLabels
        ? categoriesLabels[item]
        : data.filter(cat => cat.slug === activeCategories[item])[0].name
    } else {
      buttonLabel = itemSelected.name
    }
  } else if (item === COLOR && activeFilter) {
    buttonLabel = (
      <Fragment>
        {itemLabel}
        <span className='ad-filter__dash'>-</span>
        <FormattedMessage {...colorMessages[filtersObject[item]]} />
      </Fragment>
    )
  }

  let buttonAreaLabel = buttonLabel
  if (Object.keys(itemSelected).length > 0) {
    if (!localStorage.getItem(METER)) {
      buttonAreaLabel = `${filtersObject[MIN_AREA]
        ? meterToFeet(filtersObject[MIN_AREA])
        : data.min} ${UNIT_LABELS[FEET]} - ${filtersObject[MAX_AREA]
        ? meterToFeet(filtersObject[MAX_AREA])
        : data.max} ${UNIT_LABELS[FEET]}`
    } else {
      buttonAreaLabel = `${filtersObject[MIN_AREA]
        ? filtersObject[MIN_AREA]
        : data.min} ${UNIT_LABELS[METER]} - ${filtersObject[MAX_AREA]
        ? filtersObject[MAX_AREA]
        : data.max} ${UNIT_LABELS[METER]}`
    }
  }
  let renderMobileComponent = null
  if (isMobile && isSmall && isInside) {
    let listContentMobile
    let listTitleMobile = activeFilter
    const mobileListProps = { item, activeFilter, contentClasses, containerClasses, onClick: handleMobileInsideFilterButton }

    if (item === AREA) {
      listContentMobile = <AreaFilter onApply={onApplyAreaFilter} {...data} />
      listTitleMobile = activeFilter
        ? <Fragment>
          {itemLabel}
          <span className='ad-filter__dash'>-</span>
          {<span dangerouslySetInnerHTML={{ __html: buttonAreaLabel }} />}
        </Fragment>
        : itemLabel
      renderMobileComponent = <MobileList content={listContentMobile} title={listTitleMobile} mobileListProps={mobileListProps} />
    }
    if (item === COLOR) {
      listContentMobile = <ColorFilters onClick={onClickItem} />
      listTitleMobile = activeFilter ? buttonLabel : itemLabel
      renderMobileComponent = <MobileList content={listContentMobile} title={listTitleMobile} mobileListProps={mobileListProps} />
    }
    if (item === MATERIALS || item === YEAR || item === PRODUCT_TYPE) {
      listContentMobile = filteredData.map((item, index) =>
        <Button key={item + index + 'mobile'} onClick={e => onClickItem(e, item, index)}>
          {item.name}
        </Button>)
      listTitleMobile = itemSelected.hasOwnProperty(NAME)
        ? <Fragment>
          {itemLabel}
          <span className='ad-filter__dash'>{`- ${itemSelected.name}`}</span>
        </Fragment>
        : itemLabel
      renderMobileComponent = <MobileList content={listContentMobile} title={listTitleMobile} mobileListProps={mobileListProps} />
    }
  }

  const MobileHeaderProps = { onResetFilters, cancelCallback: () => showDispatch({ type: 'HIDE' }) }
  const mobileOnClickCb = e => {
    if (item === BIM) {
      onClickItem(e, 'active')
    } else {
      sendClickEventToInsights(`search-${searchType}-filters`, 'open', `${item}-filter`, 'mobile-filter', 'click')
      showDispatch({ type: 'SHOW' })
    }
  }
  const contentViewProps = { query, filteredData, renderFilter, renderNoMatchQuery, isOneColumn, isLoading: loadingAsyncData }
  const headerProps = { query, item, searchType, handleQuery, category, isAreaFilter: !isNotArea }
  const onClickShow = show => {
    if (show) {
      sendClickEventToInsights(`search-${searchType}-filters`, 'open', `${item}-filter`, 'desktop-filter', 'click')
    }
    showDispatch({ type: 'TOGGLE_FILTER', payload: show })
  }

  return !isMobile
    ? !STANDALONE_FILTERS.includes(item)
      ? <Dropdown
        activeButton={activeFilter}
        onClickReset={resetSelectedItem}
        innerLabelHTML={!isNotArea}
        label={isNotArea ? buttonLabel : buttonAreaLabel}
        onClickShow={show => onClickShow(show)}
        forceOpen={forceOpen}
        setForceOpen={setForceOpen}
      >
        {(onCloseDropdown, position) => (
          <div className={containerClasses} style={{ left: isBig ? 0 : `${position}px` }}>
            {<HeaderView {...headerProps} onClick={onCloseDropdown} />}
            {!standaloneWithDropdown.includes(item)
              ? <ContentView {...contentViewProps} />
              : renderFilter}
          </div>
        )}
      </Dropdown>
      : renderFilter
    : (
      <Fragment>
        {!isInside
          ? (
            <Button
              onClick={activeFilter ? resetSelectedItem : mobileOnClickCb}
              active={activeFilter}>
              {buttonLabel}
            </Button>
          )
          : renderMobileComponent
        }
        {
          modalOn && ReactDOM.createPortal(
            <MobileContent
              containerClasses={containerClasses}
              headerBarProps={MobileHeaderProps}
              headerProps={headerProps}
              itemLabel={itemLabel}
              contentViewProps={contentViewProps}
            />,
            document.body
          )
        }
      </Fragment>
    )
}

Filter.propTypes = {
  /**
   * Filter's data
   */
  data: oneOfType([array, object]),
  /**
   * Data of the categories tree for Projects view
   */
  categoriesTreeData: array,
  /**
   * Filter's name
   */
  item: string,
  /**
   * Css classes for content div
   */
  contentClasses: string,
  /**
   * Status for a mobile filter
   */
  isMobile: bool,
  /**
   * Status for a mobile filter when is inside of button "Filter"
   */
  isInside: bool,
  /**
   * Function that is fired to make the request when an item is selected or removed
   */
  requestWithRouting: func,
  /**
   * Function that request the content for a selected filter
   */
  onShowFilter: func,
  /**
   * Object with labels for categories levels
   */
  categoriesLabels: object,
  /**
   * String to know when categories_1 and categories_2 must be merged
   */
  joinCategoriesLabels: string,
  /**
   * Function that actives joinCategoriesLabels status
   */
  setJoinCategoriesLabels: func,
  /**
   * Function that removes joinCategoriesLabels status
   */
  removeJoinCategoriesLabels: func,
  /**
   * Function to clean all mobile filters inside of "Filter" button
   */
  onResetFilters: func,
  /**
   * Search's type
   */
  searchType: string,
  /**
   * Site
   */
  site: string
}

export default React.memo(Filter)
